import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageSEO from '@/components/base/PageSEO';
import { projects } from '@/mocks/projects';

import ProjectHero from './components/ProjectHero';
import ProjectOverview from './components/ProjectOverview';
import PlotSizes from './components/PlotSizes';
import PaymentPlan from './components/PaymentPlan';
import VideoShowcase from './components/VideoShowcase';
import KeyFeatures from './components/KeyFeatures';
import ProjectGallery from './components/ProjectGallery';
import LocationSection from './components/LocationSection';
import ProjectFAQ from './components/ProjectFAQ';
import FinalCTA from './components/FinalCTA';
import StickyCTA from './components/StickyCTA';

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const project = projects.find((p) => p.id === id);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <PageSEO
          title="Project Not Found — Sunshine Real Estate"
          description="The project you are looking for does not exist or may have been removed. Browse our verified property listings."
          canonicalPath={`/projects/${id}`}
          noIndex
        />
        <div className="text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#d4853a]/10 text-[#d4853a] mx-auto mb-5">
            <i className="ri-folder-unknow-line text-3xl" />
          </div>
          <h2 className="text-xl md:text-2xl font-extrabold text-charcoal-300 mb-3">Project Not Found</h2>
          <p className="text-sm text-charcoal-50 mb-6 max-w-md mx-auto">
            The project you are looking for does not exist or may have been removed.
          </p>
          <button
            onClick={() => navigate('/projects')}
            className="inline-flex items-center gap-2 px-7 py-3 bg-[#d4853a] text-white text-[13px] font-bold rounded-xl hover:bg-[#c27630] transition-all duration-300"
          >
            <i className="ri-arrow-left-line" />
            View All Projects
          </button>
        </div>
      </div>
    );
  }

  const allImages = [project.image, ...project.gallery].filter(Boolean);

  return (
    <div className="relative min-h-screen bg-[#FAFAF8]">
      <PageSEO
        title={`${project.title} — ${project.location} | Sunshine Real Estate`}
        description={`${project.title} in ${project.location}. ${project.plotSizes.join(', ')} available. ${project.priceFrom} starting price. ${project.status} — ${project.paymentPlan ? project.paymentPlan.substring(0, 120) : 'Book your site visit today with Sunshine Real Estate.'}`}
        keywords={`${project.title}, ${project.location}, ${project.category.toLowerCase()} property Pakistan, ${project.plotSizes.join(', ')}, real estate investment Islamabad, Sunshine Real Estate`}
        canonicalPath={`/projects/${project.id}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'RealEstateListing',
          name: project.title,
          description: project.description,
          url: `${SITE_URL}/projects/${project.id}`,
          image: project.image,
          datePosted: '2026-06-03',
          offers: {
            '@type': 'Offer',
            price: project.priceFrom.replace('PKR ', '').replace(/,/g, ''),
            priceCurrency: 'PKR',
            availability: 'https://schema.org/InStock',
          },
          about: {
            '@type': 'Place',
            name: project.title,
            address: {
              '@type': 'PostalAddress',
              addressLocality: project.location,
              addressCountry: 'PK',
            },
          },
        }}
      />
      <Navbar />

      {/* 1. Cinematic Hero */}
      <ProjectHero project={project} />

      {/* 2. Overview + Sticky Inquiry Card */}
      <ProjectOverview project={{ description: project.description, paymentPlan: project.paymentPlan }} />

      {/* 3. Plot Sizes Investment Cards */}
      <PlotSizes plotSizes={project.plotSizes} priceFrom={project.priceFrom} />

      {/* 4. Payment Plan Infographic */}
      <PaymentPlan />

      {/* 5. Cinematic Video Showcase */}
      <VideoShowcase projectTitle={project.title} coverImage={project.image} />

      {/* 6. Key Features */}
      <KeyFeatures />

      {/* 7. Immersive Gallery */}
      {allImages.length > 1 && (
        <ProjectGallery images={allImages} projectTitle={project.title} />
      )}

      {/* 8. Location + Map */}
      <LocationSection
        mapEmbed={project.mapEmbed}
        locationName={project.location}
      />

      {/* 9. Premium FAQ */}
      {project.faq && project.faq.length > 0 && (
        <ProjectFAQ faq={project.faq} />
      )}

      {/* 10. Final CTA */}
      <FinalCTA projectTitle={project.title} />

      <Footer />
      <WhatsAppFloat />
      <StickyCTA />
    </div>
  );
}